/**
 * Core Daily Word (Wordle) Game Types
 *
 * All TypeScript types and interfaces for the 5-letter word guessing game.
 */

// ============================================================================
// Letter & Tile States
// ============================================================================

/** State of a single letter tile after evaluation */
export type LetterState = 'correct' | 'present' | 'absent' | 'empty' | 'tbd';

/** A single evaluated letter with its state */
export interface LetterResult {
  letter: string;
  state: LetterState;
}

/** Result of evaluating a full guess */
export interface GuessResult {
  letters: LetterResult[];
  isValid: boolean;
  isCorrect: boolean;
}

// ============================================================================
// Keyboard State
// ============================================================================

/** Tracks the state of each letter on the on-screen keyboard */
export type KeyboardState = Record<string, LetterState>;

// ============================================================================
// Game State
// ============================================================================

export type WordleGameStatus = 'idle' | 'playing' | 'won' | 'lost';

export type WordleGameMode = 'daily' | 'practice' | 'competitive';

export interface WordleGameState {
  /** The target word to guess (uppercase) */
  targetWord: string;
  /** Array of submitted guesses */
  guesses: GuessResult[];
  /** Current guess being typed (not yet submitted) */
  currentGuess: string;
  /** Current row index (0-5) */
  currentRow: number;
  /** Game status */
  gameStatus: WordleGameStatus;
  /** Game mode */
  mode: WordleGameMode;
  /** Whether hard mode is enabled */
  hardMode: boolean;
  /** Keyboard state tracking */
  keyboardState: KeyboardState;
  /** Unique game identifier */
  gameId: string;
  /** Timestamp when the game started */
  startedAt: number;
  /** Timestamp when the game ended */
  endedAt?: number;
  /** Elapsed time in seconds */
  timer: number;
  /** The daily puzzle number (for daily mode) */
  puzzleNumber?: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface WordleGameSettings {
  mode: WordleGameMode;
  hardMode: boolean;
}

// ============================================================================
// Statistics
// ============================================================================

export interface WordleStats {
  gamesPlayed: number;
  gamesWon: number;
  winPercentage: number;
  currentStreak: number;
  maxStreak: number;
  guessDistribution: number[]; // Index 0 = won in 1, index 5 = won in 6
  averageGuesses: number;
  lastPlayedDate?: string;
  lastCompletedPuzzle?: number;
}

export interface WordleGameResult {
  won: boolean;
  guessCount: number;
  mode: WordleGameMode;
  puzzleNumber?: number;
  timeSeconds: number;
  hardMode: boolean;
  date: string;
}

// ============================================================================
// Multiplayer / Competitive
// ============================================================================

export interface CompetitiveWordleState {
  roomCode: string;
  targetWord: string;
  players: CompetitivePlayer[];
  status: 'waiting' | 'playing' | 'finished';
  startedAt?: number;
}

export interface CompetitivePlayer {
  id: string;
  name: string;
  guessCount: number;
  completed: boolean;
  won: boolean;
  timeSeconds: number;
}

// ============================================================================
// Constants
// ============================================================================

export const MAX_GUESSES = 6;
export const WORD_LENGTH = 5;
export const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Keyboard layout rows */
export const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE'],
];

// ============================================================================
// Component Props
// ============================================================================

export interface WordleGameProps {
  onExit: () => void;
  initialRoomCode?: string;
}
